import { useEffect, useState } from 'react';
import { ModuleActivity } from '../../services/moduleActivity';
import { letzteAktivitaet, zaehleMitarbeiter, zaehleTeilnahmenBestanden } from '../../services/sifa/sifaService';
import { KENNZAHLEN_LEER, SifaKennzahlen } from '../../services/sifa/betreuungTypes';
import { ladeKennzahlen, letzteBetreuungAktivitaet } from '../../services/sifa/sifaBetreuungService';

export interface SifaHubData {
  /** Bestandene Teilnahmen ueber alle Unterweisungen des Projekts. */
  teilnahmenBestanden: number;
  /** Personen im Stamm (ohne zusammengefuehrte Dubletten). */
  mitarbeiter: number;
  /** 'unterweisung' | 'mitarbeiter' | 'fristen' | 'taetigkeiten' | 'gbu' fuer die Kacheln (mergeAktivitaet im Hub). */
  activity: ModuleActivity;
  /** Fristen- und Betreuungszeit-Kennzahlen (Nr. 92); KENNZAHLEN_LEER solange nicht geladen. */
  kennzahlen: SifaKennzahlen;
}

const LEER: SifaHubData = { teilnahmenBestanden: 0, mitarbeiter: 0, activity: {}, kennzahlen: KENNZAHLEN_LEER };

/**
 * Zaehler und letzte Aktivitaet der SiFa-Kacheln im Projekt-Hub.
 *
 * `projectId` null = kein SiFa-Projekt: dann wird NICHTS geladen. Das ist
 * der ganze Zweck des Hooks — SiGeKo-Projekte sollen die drei sifa_*-Tabellen
 * nie anfragen. Alle Abfragen sind fail-open (fehlende Tabelle → leer).
 * Gezaehlt wird mit `head: true` (kein Zeilentransfer): fuer zwei Zahlen
 * alle Teilnahmen samt Unterschriftsbildern zu laden, war zu teuer.
 *
 * Der Ladestand traegt das Projekt, fuer das er gilt: beim Projektwechsel
 * zeigt der Hub so nie die Zahlen des vorigen Projekts.
 */
export const useSifaHubData = (projectId: string | null | undefined): SifaHubData => {
  const [stand, setStand] = useState<{ fuer: string | null; werte: SifaHubData }>({ fuer: null, werte: LEER });

  useEffect(() => {
    if (!projectId || projectId === 'temp-id') return;
    let aktiv = true;
    Promise.all([
      zaehleTeilnahmenBestanden(projectId).catch(() => 0),
      zaehleMitarbeiter(projectId).catch(() => 0),
      letzteAktivitaet(projectId).catch(() => ({ unterweisung: null, mitarbeiter: null })),
      // Nr. 92 (Fristen, Taetigkeiten): fail-open — ohne Tabellen bleiben
      // die Kacheln bei 0 und „noch nichts", der Hub laeuft weiter.
      ladeKennzahlen(projectId).catch(() => KENNZAHLEN_LEER),
      letzteBetreuungAktivitaet(projectId).catch(() => ({ fristen: null, taetigkeiten: null, gbu: null })),
    ]).then(([teilnahmenBestanden, mitarbeiter, aktivitaet, kennzahlen, betreuung]) => {
      if (!aktiv) return;
      setStand({
        fuer: projectId,
        werte: {
          teilnahmenBestanden,
          mitarbeiter,
          activity: {
            unterweisung: aktivitaet.unterweisung,
            mitarbeiter: aktivitaet.mitarbeiter,
            fristen: betreuung.fristen,
            taetigkeiten: betreuung.taetigkeiten,
            gbu: betreuung.gbu,
          },
          kennzahlen,
        },
      });
    }).catch((e) => console.warn('SiFa-Kachelwerte konnten nicht geladen werden:', e));
    return () => { aktiv = false; };
  }, [projectId]);

  return stand.fuer === projectId ? stand.werte : LEER;
};
